import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { taxCodeActions } from "../slices/taxCode";
import { getErrorMessage } from "../../utils/errorUtils";
import { TaxCodeRequestDto } from "../../shared/dtos/taxCode/TaxCodeRequestDto";
import { TaxCodeResponseDto } from "../../shared/dtos/taxCode/TaxCodeResponseDto";
import { UpdateTaxCodePayload } from "../../shared/interfaces/taxCode/UpdateTaxCodePayload";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getTaxCodes =
  (params?: PaginationQueryDto) => async (dispatch: AppDispatch) => {
    dispatch(taxCodeActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<TaxCodeResponseDto>>
      >("/tax-codes", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(taxCodeActions.setItems(data.data.content));
        dispatch(taxCodeActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(taxCodeActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getTaxCodeById = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(taxCodeActions.setLoadingFetchOne());
  try {
    const { data } = await axiosInstance.get<
      ApiResponseDto<TaxCodeResponseDto>
    >(`/tax-codes/${id}`, {
      headers: { requiresAuth: true },
    });
    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(taxCodeActions.setItem(data.data));
    }
  } catch (error) {
    dispatch(taxCodeActions.setErrorFetchOne(getErrorMessage(error)));
  }
};

export const createTaxCode =
  (values: TaxCodeRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(taxCodeActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<TaxCodeResponseDto>
      >("/tax-codes", values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Tax code created!");
        dispatch(taxCodeActions.createSuccess(data.data));
        dispatch(
          taxCodeActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(taxCodeActions.setErrorCreate(message));
    }
  };

export const updateTaxCode =
  (payload: UpdateTaxCodePayload) => async (dispatch: AppDispatch) => {
    dispatch(taxCodeActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<TaxCodeResponseDto>
      >(`/tax-codes/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Tax code updated!");
        dispatch(taxCodeActions.updateSuccess(data.data));
        dispatch(
          taxCodeActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      dispatch(taxCodeActions.setErrorUpdate(getErrorMessage(error)));
    }
  };

export const deleteTaxCode = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(taxCodeActions.setLoadingDelete());
  try {
    const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
      `/tax-codes/${id}`,
      {
        headers: { requiresAuth: true },
      },
    );
    if (data.status === ApiResponseStatus.SUCCESS) {
      toast.success(data.message || "Tax code deleted!");
      dispatch(taxCodeActions.deleteSuccess(id));
      dispatch(
        taxCodeActions.setServerStatus({
          operation: "delete",
          status: data.statusCode || 200,
        }),
      );
    }
  } catch (error) {
    dispatch(taxCodeActions.setErrorDelete(getErrorMessage(error)));
  }
};

export const resetTaxCodeServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(taxCodeActions.setServerStatus(null));
};
